import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, CalendarDays } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function nowTime() {
  return new Date().toTimeString().slice(0, 5);
}
function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export default function TrackerLogger({ cfg, onChange }) {
  const { type, levels, noteLabel, notePlaceholder } = cfg;
  const [entries, setEntries] = useState([]);
  const [weekTotal, setWeekTotal] = useState(0);
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState(nowTime());
  const [intensity, setIntensity] = useState(levels[0]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/trackers/${type}?range=30`).then((r) => {
      setEntries(r.data.entries || []);
      setWeekTotal(r.data.week_total || 0);
    }).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);

  const add = async () => {
    setBusy(true);
    try {
      await api.post(`/trackers/${type}`, { date, time, intensity, note });
      setNote("");
      setTime(nowTime());
      toast.success("Entry logged, it now feeds your Track trends");
      load();
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save entry");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/trackers/${type}/${id}`).catch(() => {});
    load();
    onChange && onChange();
  };

  return (
    <div data-testid="tracker-logger">
      <div className="bg-white rounded-2xl card-shadow p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-lg font-bold text-ink">Log an entry</h2>
          <span className="text-xs text-ink-muted" data-testid="tracker-week-total">{weekTotal} this week</span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-semibold text-ink">Date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="tracker-date"
              className="mt-1 w-full rounded-xl border border-subtle bg-app px-3 py-2.5 text-ink text-sm outline-none focus:border-[var(--brand)]" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-ink">Time</span>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="tracker-time"
              className="mt-1 w-full rounded-xl border border-subtle bg-app px-3 py-2.5 text-ink text-sm outline-none focus:border-[var(--brand)]" />
          </label>
        </div>

        <div className="mt-4">
          <span className="text-xs font-semibold text-ink">How strong</span>
          <div className="mt-2 flex gap-2">
            {levels.map((lvl) => (
              <button
                key={lvl}
                type="button"
                data-testid={`tracker-level-${lvl.toLowerCase()}`}
                onClick={() => setIntensity(lvl)}
                className={`flex-1 py-2.5 rounded-full text-sm font-semibold border transition-colors ${
                  intensity === lvl ? "bg-brand text-white border-transparent" : "bg-app text-ink-soft border-subtle"
                }`}
              >
                {lvl}
              </button>
            ))}
          </div>
        </div>

        <label className="block mt-4">
          <span className="text-xs font-semibold text-ink">{noteLabel}</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="tracker-note"
            placeholder={notePlaceholder}
            className="mt-1 w-full rounded-xl border border-subtle bg-app px-3 py-2.5 text-ink text-sm outline-none focus:border-[var(--brand)] resize-none" />
        </label>

        <button
          data-testid="tracker-add"
          onClick={add}
          disabled={busy}
          className="mt-4 w-full bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
        >
          <Plus size={16} /> {busy ? "Saving…" : "Add entry"}
        </button>
      </div>

      {entries.length > 0 && (
        <div className="mt-4">
          <p className="overline-label">Your entries (last 30 days)</p>
          <div className="mt-3 space-y-2" data-testid="tracker-entries">
            {entries.map((e) => (
              <div key={e.id} className="bg-white rounded-xl card-shadow px-4 py-3 flex items-start gap-3" data-testid="tracker-entry">
                <div className="w-9 h-9 rounded-full bg-sage flex items-center justify-center flex-shrink-0">
                  <CalendarDays size={15} className="text-brand" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-ink">{fmtDate(e.date)}</span>
                    {e.time && <span className="text-ink-muted">{e.time}</span>}
                    {e.intensity && <span className="text-xs font-semibold text-brand bg-sage px-2 py-0.5 rounded-full">{e.intensity}</span>}
                  </div>
                  {e.note && <p className="text-ink-soft text-sm mt-1 break-words">{e.note}</p>}
                </div>
                <button data-testid="tracker-delete" onClick={() => remove(e.id)} className="text-ink-muted hover:text-[var(--accent-c)] p-1">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
